/**
 * print/ArtifactPreviewFrame — Mode 2 graphite chrome around a
 * Mode 1 artifact preview.
 *
 * The contrast is deliberate: the artifact itself reads as a
 * preserved memory object (atmospheric, walnut, archival), while
 * the room around it reads as operational (graphite, restrained,
 * stewardship). The host is *holding the night up to the light*,
 * not editing a SaaS form.
 *
 * What lives here:
 *   • the print canvas itself (passed in as `children`)
 *   • a calm header with the artifact name + format selector
 *   • the export quiet-press
 *   • optional preview-mark toggle
 *
 * What does NOT live here:
 *   • progress bars, % complete chrome, success banners
 *   • coloured pills, neon accents, dashboard widgets
 *   • the artifact's own interior — that stays in the canvas
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  MODE_TWO,
  Eyebrow, EditorialHeading, GraphitePanel, MutedDivider,
  QuietPress,
} from '../ModeTwoPrimitives';
import { formatsForFamily, getFormat } from '../../lib/print/printFormats';
import { brandToast } from '../../lib/brandToast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArtifactPreviewFrame = ({
  event,
  family,            // 'polaroid_strip' | 'poster' | 'guestbook'
  artifactLabel,     // e.g. "Polaroid strip"
  exportEndpoint,    // e.g. `/api/events/{id}/artifacts/polaroid-strip`
  exportQuery,       // dict of query params (format, photo_ids…)
  formatId,
  onFormatChange,
  showMarks,
  onShowMarksChange,
  children,          // the PrintCanvas + artifact composition
  testId = 'artifact-preview-frame',
}) => {
  const [exporting, setExporting] = useState(false);
  const availableFormats = formatsForFamily(family);
  const fmt = getFormat(formatId);

  const handleExport = async () => {
    if (!fmt || exporting) return;
    // Defensive — if event hasn't resolved, the export endpoint
    // would 404 silently. The button is disabled in this case via
    // the parent passing an empty exportEndpoint; this guard is
    // belt-and-braces against any caller building the URL eagerly.
    if (!exportEndpoint || exportEndpoint.endsWith('/')) {
      brandToast.slipped('That night isn’t ready yet ✦');
      return;
    }
    setExporting(true);
    try {
      const url = `${API}${exportEndpoint}`;
      const params = { format: fmt.id, ...exportQuery };
      const r = await axios.get(url, { params, responseType: 'blob' });
      // Trigger a calm save — the file lands in Downloads as an
      // archival object, not a UI surprise.
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (event?.slug || event?.event_id || 'night').replace(/[^a-zA-Z0-9._-]/g, '-');
      a.href = objectUrl;
      a.download = `eventdrop-${family}-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (_) {
      brandToast.slipped('The export slipped · please try again');
    } finally {
      setExporting(false);
    }
  };

  return (
    <GraphitePanel
      testId={testId}
      padding="clamp(22px, 2.6vw, 32px)"
      style={{ marginBottom: 16 }}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 14,
        flexWrap: 'wrap', marginBottom: 12,
      }}>
        <Eyebrow>✦ STUDIO</Eyebrow>
        <EditorialHeading size="h2">
          <span data-testid="artifact-label">{artifactLabel}</span>
        </EditorialHeading>
      </div>
      <div style={{
        fontFamily: MODE_TWO.serif, fontStyle: 'italic',
        fontSize: 13.5, color: MODE_TWO.inkMuted, lineHeight: 1.55,
        maxWidth: 560,
      }}>
        Hold the night up to the light. The strip you see is the
        strip we’ll send to press — bleed, trim and safe area are
        all true at scale.
      </div>

      <MutedDivider />

      {/* Two-column: preview on the left, operational chrome on
          the right. On narrow screens the chrome wraps below. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(220px, 1fr)',
        gap: 'clamp(22px, 3vw, 38px)',
        alignItems: 'start',
      }}
      className="artifact-frame-grid"
      >
        <div data-testid="artifact-preview-stage" style={{
          display: 'flex', justifyContent: 'center', padding: '8px 0',
        }}>
          {children}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* Format selector — restrained register, never a coloured
              pill row. List of italic radio rows. */}
          <div>
            <Eyebrow>✦ FORMAT</Eyebrow>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {availableFormats.map((f) => {
                const active = f.id === formatId;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => onFormatChange?.(f.id)}
                    data-testid={`artifact-format-${f.id}`}
                    data-active={active ? '1' : '0'}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '6px 0',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: MODE_TWO.serif,
                      fontStyle: 'italic',
                      fontSize: 13.5,
                      color: active ? MODE_TWO.ink : MODE_TWO.inkMuted,
                      letterSpacing: '0.005em',
                    }}
                  >
                    <span style={{ color: active ? MODE_TWO.gold : MODE_TWO.inkFaint, marginRight: 8 }}>
                      {active ? '✦' : '·'}
                    </span>
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Marks toggle — quiet, single line */}
          {typeof showMarks === 'boolean' && (
            <button
              type="button"
              onClick={() => onShowMarksChange?.(!showMarks)}
              data-testid="artifact-marks-toggle"
              data-active={showMarks ? '1' : '0'}
              style={{
                background: 'transparent', border: 'none',
                padding: 0, textAlign: 'left', cursor: 'pointer',
                fontFamily: MODE_TWO.serif, fontStyle: 'italic',
                fontSize: 12.5,
                color: showMarks ? MODE_TWO.ink : MODE_TWO.inkMuted,
                letterSpacing: '0.005em',
              }}
            >
              <span style={{ color: MODE_TWO.gold, marginRight: 8 }}>{showMarks ? '✦' : '·'}</span>
              {showMarks ? 'Showing trim & safe guides' : 'Hide trim & safe guides'}
            </button>
          )}

          <div>
            <Eyebrow>✦ EXPORT</Eyebrow>
            <div style={{ marginTop: 12 }}>
              <QuietPress
                onClick={handleExport}
                disabled={exporting || !fmt}
                testId="artifact-export-btn"
              >
                {exporting ? 'Composing ✦' : 'Send to press ✦'}
              </QuietPress>
            </div>
            <div style={{
              marginTop: 10, fontFamily: MODE_TWO.serif, fontStyle: 'italic',
              fontSize: 12, color: MODE_TWO.inkFaint, lineHeight: 1.5,
            }}>
              Downloads a print-ready PDF at {fmt?.dpi || 300} DPI.
              No ordering yet — this is for your archive.
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .artifact-frame-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </GraphitePanel>
  );
};

export default ArtifactPreviewFrame;
